/* Staffing embed for chat — renders the `staffing` fenced block the
 * `staffing-query` skill emits.
 *
 * When the spec carries structured `params` (search / projectId /
 * weeks) we mount the fully-interactive <StaffingEmbed> (same chart +
 * expandable table + cell-edit dialog as the Staffing page, scoped to
 * the chat's filter). Older specs without params fall back to a
 * static read-only render of the embedded snapshot.
 */
import React from 'react';
import { StaffingEmbed } from './staffing.jsx';
import { fmtPct } from './primitives.jsx';

export const StaffingFence = ({ spec }) => {
  const p = spec?.params;
  if (p) {
    return <StaffingEmbed search={p.search || ''} projectId={p.projectId || ''} weeks={p.weeks || 12} userIds={Array.isArray(p.userIds) ? p.userIds : null}/>;
  }
  return <StaticStaffing spec={spec}/>;
};

/* Fallback: static snapshot from the spec (no live fetch). */
const StaticStaffing = ({ spec }) => {
  const weeks = spec?.weeks || [];
  const cap   = spec?.capacityByWeek || [];
  const rows  = spec?.rows || [];
  const available = spec?.available || 0;
  const max = React.useMemo(() => {
    let m = available || 100;
    for (const c of cap) {
      const t = (c.billable||0)+(c.internal||0)+(c.vacation||0)+(c.holiday||0)+(c.sick||0)+(c.free||0);
      if (t > m) m = t;
    }
    return m || 100;
  }, [cap, available]);
  const colW = 52, nameW = 150;
  const minWidth = nameW + weeks.length * colW;
  return (
    <div style={{border:'1px solid var(--border)', borderRadius:8, background:'var(--bg-card)', margin:'10px 0', overflow:'hidden'}}>
      <div style={{padding:'8px 12px', borderBottom:'1px solid var(--border)', background:'var(--bg-alt)', fontSize:12, fontWeight:600, display:'flex', justifyContent:'space-between', gap:8, flexWrap:'wrap'}}>
        <span>{spec?.title || 'Staffing'}</span>
        <span style={{fontWeight:400, color:'var(--fg-muted)'}}>{Math.round(available)} h/wk available</span>
      </div>
      <div style={{overflowX:'auto', WebkitOverflowScrolling:'touch'}}>
        <div style={{minWidth}}>
          <div style={{display:'grid', gridTemplateColumns:`${nameW}px repeat(${weeks.length}, ${colW}px)`, alignItems:'end', minHeight:110, padding:'4px 0'}}>
            <div style={{fontSize:10.5, color:'var(--fg-muted)', padding:'0 10px'}}>Capacity (h)</div>
            {weeks.map((w, i) => {
              const c = cap[i] || {}; const h = v => `${Math.max(0, (v / max) * 90)}px`;
              return (
                <div key={w.isoWeek} style={{padding:'0 3px', display:'flex', flexDirection:'column', alignItems:'center'}}>
                  <div style={{width:'100%', display:'flex', flexDirection:'column-reverse', border: w.current ? '1px solid var(--orange)' : '1px solid transparent', borderRadius:3}}>
                    <div style={{height:h(c.billable), background:'var(--orange)'}}/>
                    <div style={{height:h(c.internal), background:'#715AAE'}}/>
                    <div style={{height:h(c.vacation), background:'#3CA88A'}}/>
                    <div style={{height:h(c.holiday),  background:'#A0A0A0'}}/>
                    <div style={{height:h(c.sick),     background:'#C25450'}}/>
                    <div style={{height:h(c.free),     background:'rgba(255,100,40,0.15)'}}/>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{display:'grid', gridTemplateColumns:`${nameW}px repeat(${weeks.length}, ${colW}px)`, borderTop:'1px solid var(--border)', background:'var(--bg-alt)', fontSize:10.5, fontWeight:600}}>
            <div style={{padding:'6px 10px'}}>Person</div>
            {weeks.map(w => <div key={w.isoWeek} style={{padding:'6px 2px', textAlign:'center', background: w.current ? 'var(--orange)' : 'transparent', color: w.current ? '#fff' : 'inherit'}}>CW{w.week}</div>)}
          </div>
          {rows.map((p, ri) => (
            <div key={ri} style={{display:'grid', gridTemplateColumns:`${nameW}px repeat(${weeks.length}, ${colW}px)`, borderTop:'1px solid var(--border)', fontSize:11.5}}>
              <div style={{padding:'6px 10px', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis'}}>{p.name}</div>
              {weeks.map((w, i) => {
                const pct = (p.pct || [])[i] || 0; const under = pct > 0 && pct < 80;
                return <div key={w.isoWeek} style={{padding:'6px 2px', textAlign:'center', position:'relative', background: w.current ? 'rgba(255,100,40,0.08)' : 'transparent', fontWeight: pct ? 600 : 400}}>{pct ? `${fmtPct(pct)}%` : '·'}{under && <span style={{position:'absolute', top:2, right:4, color:'var(--orange)', fontWeight:700, fontSize:10}}>!</span>}</div>;
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
